import React, { useCallback, useContext, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { isEmpty } from 'underscore';

import { shouldReloadCharts } from 'redux/selectors/analytics';

import useScript from 'shared/hooks/use-script';
import { useSurveyPanelsInitializer } from 'analytics_dashboard/hooks/use-initialize-qrvey';

import { useAppDispatch } from 'redux/store';
import { surveyCharts } from 'redux/actions/analytics';
import { config } from '../../../../config/config.json';
import QrveyComponent from '../qrvey-component';
import QrveyPanels from '../qrvey-panels';
import { AnalyticsContext } from '../analytics-dashboard';

const SurveyCharts: React.FC = () => {
  const { institutionId, catalogId,
    prePostSurveyConfigurationId: configurationId,
  } = useContext(AnalyticsContext);
  const shouldReload = useSelector(state => shouldReloadCharts(state));

  const [surveyChartsData, setSurveyChartsData] = React.useState(null);
  const [isLoading, setIsLoading] = React.useState(false);
  const isMountedRef = useRef(true);

  const dispatch = useAppDispatch();

  const widgetStatus = useScript(config.qrvey.widgetUrl);

  const isReady = surveyChartsData && widgetStatus === 'ready';
  const { panels, isError } = useSurveyPanelsInitializer(surveyChartsData, isReady, isLoading);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const getSurveyCharts = useCallback(async () => {
    if (configurationId) {
      setIsLoading(true);
      try {
        const result = await dispatch(surveyCharts({
          institutionId,
          preCourseCatalogId: catalogId,
          configurationId,
        }));
        if (isMountedRef.current && result?.payload && !isEmpty(result.payload)) {
          setSurveyChartsData(result.payload);
        }
      } finally {
        if (isMountedRef.current) {
          setIsLoading(false);
        }
      }
    }
  }, [configurationId, catalogId, dispatch, institutionId]);

  useEffect(() => {
    // Load charts on mount and when dependencies of getSurveyCharts change
    getSurveyCharts();
  }, [getSurveyCharts]);

  useEffect(() => {
    if (shouldReload) {
      getSurveyCharts();
    }
  }, [getSurveyCharts, shouldReload]);

  return (
    surveyChartsData && !shouldReload && (
      <QrveyComponent
        panels={panels?.length > 0 ? <QrveyPanels panels={panels} /> : null}
        isReady={isReady && !isError}
        showdashboard={false}
      />
    )
  );
};

export default SurveyCharts;
